/**
 * Recurrence expansion utilities for the PWA.
 *
 * Uses the shared expandRecurrenceRule utility to generate visible occurrences
 * from stored recurrence_rule fields, so the PWA computes the same set of
 * occurrences as the desktop app.
 */

import { expandRecurrenceRule } from '@anna-calendar/shared';
import type { Event } from '@anna-calendar/shared';
import { formatDateISO } from './dates';

/**
 * A virtual event representing a single occurrence of a recurring event.
 * Same shape as Event but with adjusted start_time/end_time for the occurrence date.
 */
export type VirtualEvent = Event;

/**
 * Expand all events (including recurring) into a flat list of occurrences
 * that fall within the given date range.
 *
 * - Non-recurring events are returned as-is if they fall in range.
 * - Recurring events are expanded via rrule.js; each occurrence gets the
 *   original event's metadata (title, color, id prefix, etc.) with adjusted times.
 * - Invalid RRULE strings are silently skipped (the base event is excluded).
 *
 * @param events - Raw events from the API (may include recurring events)
 * @param rangeStart - ISO string for the start of the visible range
 * @param rangeEnd - ISO string for the end of the visible range
 */
export function expandEvents(
  events: Event[],
  rangeStart: string,
  rangeEnd: string,
): VirtualEvent[] {
  const result: VirtualEvent[] = [];

  for (const event of events) {
    if (!event.recurrence_rule) {
      result.push(event);
      continue;
    }

    const occurrences = expandRecurrenceRule(
      event.recurrence_rule,
      event.start_time,
      event.end_time,
      rangeStart,
      rangeEnd,
    );

    for (const occ of occurrences) {
      result.push({
        ...event,
        id: `${event.id}::${formatDateISO(new Date(occ.occurrenceStart))}`,
        start_time: occ.occurrenceStart,
        end_time: occ.occurrenceEnd,
      });
    }
  }

  return result;
}
